import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import VendorCard from '@/components/VendorCard';
import { Button } from '@/components/ui/button';
import { vendorCategories } from '@/lib/vendorCategories';
import { categoryNameToSeoPath } from '@/lib/vendorCategorySeo';
import { getSeoPreviewMode } from '@/lib/seoPreviewMode';
import { isCategoryEnabled, mergeOrderedCategoryObjects } from '@/lib/vendorCategorySource';
import { ArrowRight, Calendar, Inbox, MapPin, QrCode, Sparkles, Smartphone, Users, WalletCards } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultHeroImages = [
  'https://customer-assets.emergentagent.com/job_2bdcf71f-e6c4-4624-b0e3-e65b5b83b9a9/artifacts/nnrvkplz_1971-gersonana-websized-319%20copy.jpg',
  'https://customer-assets.emergentagent.com/job_2bdcf71f-e6c4-4624-b0e3-e65b5b83b9a9/artifacts/g6gk9esc_weddingfacebook-6.jpg',
  'https://customer-assets.emergentagent.com/job_2bdcf71f-e6c4-4624-b0e3-e65b5b83b9a9/artifacts/vkf2bk0q_18412-taraandstephen-websized-664%20copy.jpg',
  'https://customer-assets.emergentagent.com/job_2bdcf71f-e6c4-4624-b0e3-e65b5b83b9a9/artifacts/ifkmacvb_18-5-29-zophiaandjamie-websized-577%20copy.jpg',
  'https://customer-assets.emergentagent.com/job_2bdcf71f-e6c4-4624-b0e3-e65b5b83b9a9/artifacts/zyvhot8k_2377-aniapaul-websized-5.jpg',
  'https://customer-assets.emergentagent.com/job_2bdcf71f-e6c4-4624-b0e3-e65b5b83b9a9/artifacts/cg60gfzl_23420-mariiaronnie-websized-87.jpg',
  'https://customer-assets.emergentagent.com/job_2bdcf71f-e6c4-4624-b0e3-e65b5b83b9a9/artifacts/17k1wfyg_23915-vimlaramdeo-websized-19.jpg',
  'https://customer-assets.emergentagent.com/job_2bdcf71f-e6c4-4624-b0e3-e65b5b83b9a9/artifacts/zlgkjijy_18-7-6-janjaandlars-websized-214.jpg',
];

const plannerHighlights = [
  { icon: Sparkles, title: 'Discover Vendors', description: 'Find trusted local professionals for every part of your wedding' },
  { icon: WalletCards, title: 'Plan Your Budget', description: 'Get clarity on costs so there are no surprises' },
  { icon: Calendar, title: 'Stay Organised', description: 'Keep track of your timeline and key steps' },
  { icon: Users, title: 'Get Support When You Need It', description: 'Use tools and guidance alongside planners — or on your own' },
];

export default function HomePage() {
  const [featuredVendors, setFeaturedVendors] = useState([]);
  const [siteContent, setSiteContent] = useState(null);
  const [categoryLinks, setCategoryLinks] = useState(vendorCategories);
  const [currentHeroIndex, setCurrentHeroIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const seoPreviewMode = useMemo(() => getSeoPreviewMode(), []);

  const heroImages = siteContent?.hero_images?.length > 0 ? siteContent.hero_images : defaultHeroImages;
  const isFreeGuideEnabled = Boolean(siteContent?.free_guide_enabled);

  useEffect(() => {
    fetchData();
    seedDatabase();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHeroIndex((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [heroImages.length]);

  const seedDatabase = async () => {
    try {
      await axios.post(`${API}/seed`);
    } catch {
      // already seeded
    }
  };

  const fetchData = async () => {
    try {
      const [vendorsRes, contentRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/vendors?featured=true`),
        axios.get(`${API}/cms/content`, {
          params: { _ts: Date.now() },
          headers: {
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
          },
        }),
        axios.get(`${API}/categories`).catch(() => ({ data: [] })),
      ]);
      setFeaturedVendors(vendorsRes.data);
      setSiteContent(contentRes.data);

      const categoryApiData = Array.isArray(categoriesRes?.data) ? categoriesRes.data : [];
      const categoriesSource = seoPreviewMode
        ? categoryApiData
        : categoryApiData.filter(isCategoryEnabled);

      setCategoryLinks(mergeOrderedCategoryObjects(categoriesSource, vendorCategories));
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pb-24 md:pb-0" data-testid="home-page">
      <section className="bg-stone-100 pt-2 pb-8 md:pt-4 md:pb-10">
        <div className="container-wedding">
          <div className="max-w-4xl mx-auto text-center pt-0 pb-2 md:pb-3">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium text-[#1C1917] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              Plan Your <span className="text-[#D4AF37]">Mediterranean</span> Wedding in Gibraltar With Confidence
            </h1>
            <p className="text-base md:text-lg text-stone-600 mb-5 md:mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>
              Discover trusted local vendors, smart planning tools, and everything you need to create your perfect wedding — all in one place.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-3 md:mb-4">
              {isFreeGuideEnabled && (
                <Link to="/free-guide" data-testid="home-free-guide-link">
                  <Button className="btn-primary font-bold uppercase tracking-[0.18em] px-8 py-3" data-testid="home-find-vendors-button">
                    Get Your Free Wedding Guide
                  </Button>
                </Link>
              )}
              <Link to="/vendors" data-testid="home-browse-vendors-link">
                <Button className="bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-white font-bold uppercase tracking-[0.18em] px-8 py-3" data-testid="home-start-planning-button">
                  Browse Vendors
                </Button>
              </Link>
            </div>
          </div>

          <div className="mx-auto w-full max-w-5xl border border-stone-300 bg-white shadow-sm">
            <div className="relative aspect-[2/3] overflow-hidden bg-white">
              {heroImages.map((image, index) => (
                <img
                  key={image}
                  src={image}
                  alt="Gibraltar wedding"
                  className={`absolute inset-0 h-full w-full object-cover object-center transition-opacity duration-1000 ${index === currentHeroIndex ? 'opacity-100' : 'opacity-0'}`}
                />
              ))}
              <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-black/5" />
              <img
                src="https://customer-assets.emergentagent.com/job_17b3eba4-e170-4c07-a419-15dd5729217a/artifacts/dbazxz6j_logo%20wht%2026%20cropped.png"
                alt="Gibraltar Wedding Expo"
                className="absolute top-4 left-4 md:top-5 md:left-5 h-16 md:h-20 w-auto z-10"
                style={{ filter: 'drop-shadow(2px 2px 6px rgba(0,0,0,0.75))' }}
              />
            </div>
          </div>

          <section className="mx-auto mt-8 w-full max-w-5xl bg-gradient-to-b from-[#fffdfb] to-white px-6 py-10 md:px-8 md:py-12" data-testid="home-feature-wedding-website-section">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:items-center">
              <div className="space-y-6">
                <p className="caption" data-testid="home-feature-wedding-website-kicker">Planner Tools for Couples</p>
                <h2 className="text-4xl sm:text-5xl lg:text-6xl font-medium text-[#1C1917] leading-tight" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="home-feature-wedding-website-headline">
                  Create a FREE wedding website your guests will love.
                </h2>
                <ul className="space-y-2 text-base text-stone-700" data-testid="home-feature-wedding-website-subtext-list">
                  {['Share your story', 'Add wedding details', 'Collect RSVPs from guests'].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="mt-1 text-[#D4AF37]">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-base md:text-lg leading-relaxed text-stone-600" data-testid="home-feature-wedding-website-supporting-text">
                  Create and share your wedding website with your guests in minutes
                </p>
                <Link to="/planner" data-testid="home-feature-wedding-website-cta-link">
                  <Button className="btn-primary font-bold uppercase tracking-[0.14em] px-9 py-3.5 text-sm btn-lift shadow-lg hover:shadow-xl" data-testid="home-feature-wedding-website-cta-button">
                    START BUILDING YOUR WEDDING WEBSITE
                  </Button>
                </Link>
                <p className="text-sm text-stone-500" data-testid="home-feature-wedding-website-cta-helper">
                  Free to start — no setup needed.
                </p>
              </div>

              <div className="rounded-2xl bg-gradient-to-br from-[#fdf7f1] via-white to-[#fffaf4] p-5 md:p-6 shadow-[0_24px_60px_-32px_rgba(92,1,32,0.35)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_30px_70px_-30px_rgba(92,1,32,0.4)]" data-testid="home-feature-wedding-website-visual-card">
                <div className="overflow-hidden rounded-xl bg-white shadow-[0_16px_40px_-24px_rgba(0,0,0,0.45)]" data-testid="home-feature-wedding-website-mock-preview">
                  <div className="flex items-center justify-between border-b border-stone-100 px-4 py-2.5" data-testid="home-feature-wedding-website-mock-title">
                    <div className="flex items-center gap-1.5">
                      <span className="h-2.5 w-2.5 rounded-full bg-[#f2d5d6]" />
                      <span className="h-2.5 w-2.5 rounded-full bg-[#f3e4bf]" />
                      <span className="h-2.5 w-2.5 rounded-full bg-[#d6e9d9]" />
                    </div>
                    <p className="text-[10px] uppercase tracking-[0.15em] text-stone-500">wedding website preview</p>
                    <span className="text-[10px] font-medium rounded-full bg-[#D4AF37]/20 px-2 py-0.5 text-[#5C0120]">Live</span>
                  </div>

                  <div className="p-4 md:p-5" data-testid="home-feature-wedding-website-mock-content">
                    <div className="rounded-xl bg-gradient-to-b from-[#fff6f4] to-[#fffaf4] px-4 py-7 text-center">
                      <p className="text-[11px] uppercase tracking-[0.18em] text-[#5C0120]/70 mb-2">Olivia &amp; James</p>
                      <p className="text-2xl md:text-3xl leading-tight text-[#1C1917]" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="home-feature-wedding-website-mock-couple-names">24 June 2027 · Gibraltar</p>
                      <div className="mt-4 flex flex-wrap justify-center gap-2 text-[11px] text-stone-700" data-testid="home-feature-wedding-website-mock-nav">
                        {['Our Story', 'Wedding Day', 'RSVP'].map((item) => (
                          <span key={item} className="rounded-full bg-white px-3 py-1 shadow-sm">{item}</span>
                        ))}
                      </div>
                    </div>

                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="home-feature-wedding-website-mock-blocks">
                      <div className="rounded-lg bg-stone-50 p-3">
                        <p className="text-[10px] uppercase tracking-[0.14em] text-[#5C0120] mb-2">Our Story</p>
                        <p className="text-xs text-stone-600 leading-relaxed">A little look at how we met and the journey to our wedding day.</p>
                      </div>
                      <div className="rounded-lg bg-stone-50 p-3">
                        <p className="text-[10px] uppercase tracking-[0.14em] text-[#5C0120] mb-2">Wedding Day</p>
                        <p className="text-xs text-stone-600 leading-relaxed">Ceremony at 3:00 PM · Sunset reception at the coast.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="mx-auto mt-8 w-full max-w-5xl bg-gradient-to-b from-white to-[#fff8f0] px-6 py-10 md:px-8 md:py-12" data-testid="home-feature-photo-sharing-section">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:items-center">
              <div className="space-y-6 order-2 lg:order-1">
                <p className="caption" data-testid="home-feature-photo-sharing-kicker">Planner Tools for Couples</p>
                <h2 className="text-4xl sm:text-5xl lg:text-6xl font-medium text-[#1C1917] leading-tight" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="home-feature-photo-sharing-headline">
                  Capture every heartfelt moment from your guests
                </h2>
                <ul className="space-y-2 text-base text-stone-700" data-testid="home-feature-photo-sharing-subtext-list">
                  {['FREE Unique QR Code for your wedding', 'Guests upload and share with you their photos instantly', 'Build your shared wedding album'].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="mt-1 text-[#D4AF37]">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-base md:text-lg leading-relaxed text-stone-600" data-testid="home-feature-photo-sharing-supporting-text">
                  Let your guests capture and upload your wedding memories
                </p>
                <Link to="/planner?feature=gallery" className="block sm:inline-block" data-testid="home-feature-photo-sharing-cta-link">
                  <Button className="w-full sm:w-auto bg-[#B8860B] hover:bg-[#9f7308] text-white font-bold uppercase tracking-[0.12em] sm:tracking-[0.14em] px-6 sm:px-9 py-3.5 text-sm whitespace-normal leading-snug text-center btn-lift shadow-lg hover:shadow-xl" data-testid="home-feature-photo-sharing-cta-button">
                    Create your photo sharing page
                  </Button>
                </Link>
                <p className="text-sm text-stone-500" data-testid="home-feature-photo-sharing-cta-helper">
                  Set up your QR code in seconds.
                </p>
              </div>

              <div className="order-1 lg:order-2 rounded-2xl bg-gradient-to-br from-[#fffaf3] via-white to-[#fdf6ed] p-5 md:p-6 shadow-[0_24px_60px_-32px_rgba(212,175,55,0.45)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_30px_70px_-30px_rgba(212,175,55,0.55)]" data-testid="home-feature-photo-sharing-visual-card">
                <div className="rounded-xl bg-white p-4 md:p-5 shadow-[0_18px_44px_-24px_rgba(0,0,0,0.4)]" data-testid="home-feature-photo-sharing-mock-phone-wrap">
                  <div className="grid grid-cols-[1fr_auto_1fr_auto_1fr] items-center gap-2" data-testid="home-feature-photo-sharing-flow-row">
                    <div className="rounded-lg bg-[#faf4ea] p-3 text-center" data-testid="home-feature-photo-sharing-mock-qr-wrap">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#5C0120] mb-2">QR Code</p>
                      <div className="mx-auto h-14 w-14 rounded-md bg-white flex items-center justify-center" data-testid="home-feature-photo-sharing-mock-qr-code">
                        <QrCode className="h-8 w-8 text-[#5C0120]" />
                      </div>
                    </div>

                    <ArrowRight className="h-4 w-4 text-[#D4AF37]" data-testid="home-feature-photo-sharing-arrow-1" />

                    <div className="rounded-lg bg-stone-50 p-3 text-center" data-testid="home-feature-photo-sharing-mock-phone-scan">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#5C0120] mb-2">Scan</p>
                      <div className="mx-auto h-14 w-10 rounded-[10px] bg-white border border-stone-200 flex items-center justify-center">
                        <Smartphone className="h-6 w-6 text-[#5C0120]" />
                      </div>
                    </div>

                    <ArrowRight className="h-4 w-4 text-[#D4AF37]" data-testid="home-feature-photo-sharing-arrow-2" />

                    <div className="rounded-lg bg-[#f9f4eb] p-3" data-testid="home-feature-photo-sharing-mock-gallery-grid">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#5C0120] mb-2 text-center" data-testid="home-feature-photo-sharing-mock-phone-title">Gallery</p>
                      <div className="grid grid-cols-2 gap-1.5">
                        <div className="h-6 rounded-md bg-gradient-to-br from-[#f8e4c2] to-[#f4d6a0]" />
                        <div className="h-6 rounded-md bg-gradient-to-br from-[#f7d6dd] to-[#efbfc8]" />
                        <div className="h-6 rounded-md bg-gradient-to-br from-[#e6efe6] to-[#cedfce]" />
                        <div className="h-6 rounded-md bg-gradient-to-br from-[#e8e1f4] to-[#d7cdeb]" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

        </div>
      </section>

      <section className="bg-white border-y border-stone-200">
        <div className="container-wedding py-8">
          <div className="text-center mb-6">
            <div className="max-w-4xl mx-auto space-y-3 text-stone-700 leading-relaxed" data-testid="home-trust-positioning-text">
              <p>Planning your wedding — whether you&apos;re here in Gibraltar or joining us from abroad — should feel exciting and special.</p>
              <p>Gibraltar Wedding Expo is here to guide you along the way, giving you the confidence to make decisions that feel right for you.</p>
              <p>Whether you work with a planner or plan it yourself, this is your space to bring everything together — simply and beautifully.</p>
              <p>Create your wedding website, manage your guest list, collect RSVPs, and plan your day — all in one place, with support whenever you need it.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {plannerHighlights.map(({ icon: Icon, title, description }) => (
              <div key={title} className="border border-stone-200 bg-stone-50 px-5 py-5 text-left">
                <div className="w-10 h-10 bg-[#D4AF37]/15 flex items-center justify-center mb-3">
                  <Icon className="h-5 w-5 text-[#5C0120]" />
                </div>
                <h3 className="font-semibold mb-1">{title}</h3>
                <p className="text-sm text-stone-600 leading-relaxed">{description}</p>
              </div>
            ))}
          </div>
          {isFreeGuideEnabled && <div className="mt-8 border border-stone-200 bg-stone-50 px-6 py-6 text-center" data-testid="home-free-guide-block">
            <div className="mb-6 overflow-hidden rounded-sm border border-stone-200 bg-white" data-testid="home-free-guide-break-image-wrap">
              <div className="aspect-[16/9] w-full">
                <img
                  src="https://customer-assets.emergentagent.com/job_15dd5a89-86ae-45e9-98d1-4a6537a02f49/artifacts/m9rd81ha_1971-gersonana-websized-322%20copy.jpg"
                  alt="Gibraltar wedding couple portrait"
                  className="h-full w-full object-cover object-center"
                  data-testid="home-free-guide-break-image"
                />
              </div>
            </div>
            <h2 className="text-2xl md:text-3xl font-medium text-[#1C1917] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              Getting Married in Gibraltar? Start Here.
            </h2>
            <p className="text-stone-700 mb-4">
              Download your free Gibraltar Wedding Guide and take the first step with clarity and confidence.
            </p>
            <ul className="text-left max-w-lg mx-auto space-y-2 text-stone-700 mb-5" data-testid="home-free-guide-points">
              {['Step-by-step process', 'Realistic costs & timelines', 'Venue and supplier guidance', 'Tips for UK & international couples'].map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <span className="text-[#D4AF37] mt-1">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <Link to="/free-guide" data-testid="home-download-free-guide-link">
              <Button className="btn-primary font-bold uppercase tracking-[0.18em] px-8 py-3" data-testid="home-download-free-guide-button">
                Download Free Guide
              </Button>
            </Link>
          </div>}
        </div>
      </section>

      <section className="section-padding">
        <div className="container-wedding">
          <div className="text-center mb-10">
            <p className="caption mb-3">Browse By Category</p>
            <h2 className="heading-lg">Find Your Perfect Match</h2>
            {seoPreviewMode && (
              <p className="mt-3 text-xs font-medium text-[#5C0120]" data-testid="home-seo-preview-mode-badge">
                SEO preview mode ON — hidden categories are visible for review
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {categoryLinks.map((category, index) => (
              <Link
                key={category.slug || category.name}
                to={categoryNameToSeoPath(category.name)}
                className="group border border-stone-200 bg-white p-5 text-center transition-all duration-300 hover:border-[#D4AF37] hover:-translate-y-1"
                style={{ animationDelay: `${index * 0.04}s` }}
                data-testid={`category-${(category.slug || category.name || '').toString().toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                {category.icon === 'gib-flag' ? (
                  <img src="https://flagcdn.com/w80/gi.png" alt="Gibraltar Flag" className="h-8 w-auto mx-auto mb-3" />
                ) : (
                  <span className="text-3xl mb-3 block">{category.icon}</span>
                )}
                <h3 className="font-medium text-[#1C1917] group-hover:text-[#5C0120] transition-colors duration-200">{category.name}</h3>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-wedding">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="caption mb-3">Handpicked For You</p>
              <h2 className="heading-lg">Featured Vendors</h2>
            </div>
            <Link to="/vendors" className="hidden md:flex items-center gap-2 text-[#5C0120] hover:gap-3 transition-all duration-200">
              View All
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, index) => (
                <div key={index} className="h-[450px] bg-stone-100 animate-pulse" />
              ))}
            </div>
          ) : featuredVendors.length === 0 ? (
            <div className="text-center py-12 text-stone-500">
              <p>No featured vendors yet. Check back soon!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="home-featured-vendors-grid">
              {featuredVendors.map((vendor, index) => (
                <div key={vendor.id} className="animate-fade-in-up" style={{ animationDelay: `${index * 0.08}s` }}>
                  <VendorCard vendor={vendor} />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_2bdcf71f-e6c4-4624-b0e3-e65b5b83b9a9/artifacts/ifkmacvb_18-5-29-zophiaandjamie-websized-577%20copy.jpg"
            alt="Wedding couple in Gibraltar"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#5C0120]/75" />
        </div>

        <div className="relative container-wedding text-center">
          <p className="caption text-[#D4AF37] mb-3">How It Works</p>
          <h2 className="heading-lg text-white mb-4 max-w-2xl mx-auto">Start Planning Your Gibraltar Wedding Today</h2>
          <div className="max-w-xl mx-auto mb-5 text-white/90 text-left" data-testid="home-how-it-works-steps">
            <ol className="space-y-2 list-decimal list-inside">
              <li>Browse trusted suppliers</li>
              <li>Contact vendors directly</li>
              <li>Plan your wedding with confidence</li>
            </ol>
          </div>
          <p className="text-white/80 text-lg mb-6 max-w-xl mx-auto" style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}>
            Download your free guide and take the stress out of planning your Gibraltar wedding.
          </p>
          <div className="max-w-3xl mx-auto text-left text-white/85 mb-6 space-y-5" data-testid="home-experiences-and-suppliers-copy">
            <div>
              <h3 className="font-semibold text-white mb-2">Make your wedding more than just a ceremony.</h3>
              <p>From paddle boarding along Gibraltar’s coastline to dolphin watching, romantic dining, and unforgettable local experiences — there’s so much for you and your guests to enjoy.</p>
            </div>
            <div>
              <h3 className="font-semibold text-white mb-2">Are you a Gibraltar-based wedding supplier or experience provider?</h3>
              <p className="mb-2">We connect your business with couples who are actively planning their wedding.</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Increase your visibility</li>
                <li>Receive direct enquiries</li>
                <li>Be part of a curated platform</li>
              </ul>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {isFreeGuideEnabled && (
              <Link to="/free-guide" data-testid="home-final-get-free-guide-link">
                <Button className="bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-white uppercase font-bold tracking-[0.18em] text-xs px-8 py-4 btn-lift" data-testid="cta-register">
                  Get Free Guide
                </Button>
              </Link>
            )}
            <Link to="/vendors" data-testid="home-final-browse-vendors-link">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 uppercase font-bold tracking-[0.18em] text-xs px-8 py-4">
                Browse Vendors
              </Button>
            </Link>
            <Link to="/vendors/pricing" data-testid="home-final-join-supplier-link">
              <Button variant="outline" className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/15 uppercase font-bold tracking-[0.18em] text-xs px-8 py-4">
                Join as a Supplier
              </Button>
            </Link>
          </div>
          <div className="mt-4">
            {isFreeGuideEnabled && (
              <>
                <Link to="/free-guide" className="text-[#D4AF37] hover:text-[#D4AF37]/85 inline-flex items-center gap-2" data-testid="home-how-it-works-free-guide-link">
                  <span className="uppercase tracking-[0.15em] text-xs font-bold">Download Free Guide</span>
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <span className="mx-3 text-white/40">|</span>
              </>
            )}
            <Link to={categoryNameToSeoPath('Things to Do')} className="text-[#D4AF37] hover:text-[#D4AF37]/85 inline-flex items-center gap-2" data-testid="home-explore-experiences-link">
              <span className="uppercase tracking-[0.15em] text-xs font-bold">Explore Experiences</span>
            </Link>
            <span className="mx-3 text-white/40">|</span>
            <Link to="/vendors/pricing" className="text-[#D4AF37] hover:text-[#D4AF37]/85 inline-flex items-center gap-2" data-testid="home-join-supplier-link">
              <span className="uppercase tracking-[0.15em] text-xs font-bold">Join as a Supplier</span>
            </Link>
          </div>
        </div>
      </section>

      <section className="section-padding">
        <div className="container-wedding grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="caption mb-4">Why couples love Gibraltar!</p>
            <h2 className="heading-lg mb-6">Why get married in Gibraltar</h2>
            <div className="space-y-4 text-stone-600 leading-relaxed">
              <p>
                A unique blend of British simplicity, Mediterranean beauty, and straightforward legal requirements makes Gibraltar one of the easiest and most stunning places to get married.
              </p>
              <ul className="space-y-2 mt-6">
                {['No long residency requirements', 'Simple legal process', 'Ideal for destination weddings', 'Beautiful coastal and scenic locations'].map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <MapPin className="h-5 w-5 text-[#D4AF37] flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="overflow-hidden bg-stone-100">
              <img
                src="https://customer-assets.emergentagent.com/job_2bdcf71f-e6c4-4624-b0e3-e65b5b83b9a9/artifacts/g6gk9esc_weddingfacebook-6.jpg"
                alt="Wedding on Gibraltar beach"
                className="w-full aspect-[4/5] object-cover"
              />
            </div>
            <div className="overflow-hidden bg-stone-100 pt-8">
              <img
                src="https://customer-assets.emergentagent.com/job_2bdcf71f-e6c4-4624-b0e3-e65b5b83b9a9/artifacts/xwnkp9qp_18-7-6-janjaandlars-websized-214.jpg"
                alt="Couple in Gibraltar"
                className="w-full aspect-[4/5] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-stone-200 bg-white/95 px-4 py-3 backdrop-blur md:hidden" data-testid="home-mobile-sticky-cta">
        <div className="container-wedding flex items-center gap-2" data-testid="home-mobile-sticky-cta-actions">
          {isFreeGuideEnabled && (
            <Link to="/free-guide" className="flex-1" data-testid="home-mobile-sticky-guide-link">
              <Button className="w-full btn-primary uppercase tracking-[0.12em] font-bold text-xs" data-testid="home-mobile-sticky-guide-button">
                Get Free Guide
              </Button>
            </Link>
          )}
          <Link to="/vendors" className={isFreeGuideEnabled ? 'flex-1' : 'w-full'} data-testid="home-mobile-sticky-vendors-link">
            <Button variant="outline" className="w-full border-[#5C0120] text-[#5C0120] hover:bg-[#5C0120]/10 uppercase tracking-[0.12em] font-bold text-xs" data-testid="home-mobile-sticky-vendors-button">
              Browse Vendors
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}